/**
 * Sprint 2 — Trackingstatus CMS (Fase 1)
 *
 * Wordt gemount in LabelDetailDialog. Laat backoffice-gebruikers per
 * shipping label een eigen "5999Cargo" status zetten met:
 *  - statuscode (machine-readable slug)
 *  - publieke labels NL + EN
 *  - publieke berichten NL + EN
 *  - interne notitie (nooit publiek zichtbaar)
 *  - voortgangs-% (0..100)
 *  - override toggle die bepaalt welke statusbron actief is:
 *      uit → externe/interne fallback (W-koppeling of interne label-status)
 *      aan → 5999Cargo custom status is leidend
 *
 * RBAC (front-end weerspiegelt backend):
 *  - P_QUOTES_EDIT (Owner/Admin/Manager/Medewerker) mag het formulier
 *    invullen + opslaan.
 *  - P_QUOTES_DELETE (Owner/Admin/Manager — NIET Medewerker) mag de
 *    override toggle bedienen + custom status wissen.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Trash2,
  AlertTriangle,
  Globe,
  History as HistoryIcon,
  ChevronDown,
  ChevronUp,
  Lock,
  Wand2,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";

import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";

const EMPTY_FORM = {
  code: "",
  public_label: "",
  public_label_en: "",
  public_message: "",
  public_message_en: "",
  internal_note: "",
  progress_percent: 50,
};

const HISTORY_ACTION_LABELS = {
  tracking_custom_status_updated: "Custom status bijgewerkt",
  tracking_custom_status_cleared: "Custom status gewist",
  tracking_status_override_enabled: "Override geactiveerd",
  tracking_status_override_disabled: "Override gedeactiveerd",
  tracking_linked_to_wnumber: "Gekoppeld aan W-nummer",
  tracking_unlinked_from_wnumber: "Ontkoppeld van W-nummer",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function slugifyCode(input) {
  return (input || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_\- ]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .slice(0, 80);
}

export default function CustomStatusSection({ label, setLabel, onChanged }) {
  const { user } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const isMedewerker = userRole === "medewerker";
  const isCancelled = label.status === "geannuleerd";

  const existing = label.custom_status || null;
  const overrideActive = !!label.use_custom_override;

  if (isCancelled) {
    return null;
  }

  // Initialiseer form-state vanaf het bestaande custom_status doc bij elke
  // label of custom_status mutatie. We gebruiken een "sleutel" zodat React
  // de form-state correct herzet wanneer het label of de custom_status
  // verandert — zonder set-state-in-effect te triggeren.
  const formKey = `${label.id}::${existing?.code || ""}::${existing?.updated_at || ""}`;
  return (
    <CustomStatusFormShell
      key={formKey}
      label={label}
      setLabel={setLabel}
      onChanged={onChanged}
      existing={existing}
      overrideActive={overrideActive}
      isMedewerker={isMedewerker}
    />
  );
}

function CustomStatusFormShell({
  label,
  setLabel,
  onChanged,
  existing,
  overrideActive,
  isMedewerker,
}) {
  const initial = existing && existing.code
    ? {
        code: existing.code || "",
        public_label: existing.public_label || "",
        public_label_en: existing.public_label_en || "",
        public_message: existing.public_message || "",
        public_message_en: existing.public_message_en || "",
        internal_note: existing.internal_note || "",
        progress_percent:
          typeof existing.progress_percent === "number"
            ? existing.progress_percent
            : 50,
      }
    : EMPTY_FORM;

  const [form, setForm] = useState(initial);
  const [savingForm, setSavingForm] = useState(false);
  const [togglingOverride, setTogglingOverride] = useState(false);
  const [clearing, setClearing] = useState(false);

  // History
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Presets (Sprint 2 Fase 2)
  const [presets, setPresets] = useState([]);
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [applyingPreset, setApplyingPreset] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get("/admin/tracking-status-presets");
        if (!cancelled) setPresets(r.data?.items || []);
      } catch {
        // stilletjes negeren — sectie blijft werken zonder presets
      } finally {
        if (!cancelled) setPresetsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await adminApi.get(
        `/admin/shipping-labels/${label.id}/status-history`,
      );
      setHistory(r.data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Historie laden mislukt");
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history.length === 0) {
      await loadHistory();
    }
  };

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const formValid =
    !!form.code.trim() &&
    !!form.public_label.trim() &&
    !Number.isNaN(Number(form.progress_percent)) &&
    Number(form.progress_percent) >= 0 &&
    Number(form.progress_percent) <= 100;

  const handleSave = async () => {
    if (!formValid) {
      toast.error("Vul minimaal een code, publiek label en geldig % in.");
      return;
    }
    setSavingForm(true);
    try {
      const payload = {
        code: slugifyCode(form.code),
        public_label: form.public_label.trim(),
        public_label_en: form.public_label_en.trim() || null,
        public_message: form.public_message.trim() || null,
        public_message_en: form.public_message_en.trim() || null,
        internal_note: form.internal_note.trim() || null,
        progress_percent: Math.max(0, Math.min(100, Number(form.progress_percent))),
      };
      const r = await adminApi.patch(
        `/admin/shipping-labels/${label.id}/custom-status`,
        payload,
      );
      setLabel(r.data);
      toast.success("Custom status opgeslagen");
      onChanged && onChanged();
      setHistory([]);
    } catch (err) {
      toast.error(formatApiError(err) || "Opslaan mislukt");
    } finally {
      setSavingForm(false);
    }
  };

  const handleToggleOverride = async () => {
    if (isMedewerker) return;
    if (!existing && !overrideActive) {
      toast.error(
        "Stel eerst een custom status in voordat u de override aanzet.",
      );
      return;
    }
    setTogglingOverride(true);
    try {
      const r = await adminApi.post(
        `/admin/shipping-labels/${label.id}/override-toggle`,
        { use_custom_override: !overrideActive },
      );
      setLabel(r.data);
      toast.success(
        r.data.use_custom_override
          ? "Custom status override geactiveerd — klanten zien nu de 5999Cargo status."
          : "Override gedeactiveerd — externe / interne status is weer leidend.",
      );
      onChanged && onChanged();
      setHistory([]);
    } catch (err) {
      toast.error(formatApiError(err) || "Toggle mislukt");
    } finally {
      setTogglingOverride(false);
    }
  };

  const handleClear = async () => {
    if (isMedewerker) return;
    if (
      !window.confirm(
        "Weet u zeker dat u de custom trackingstatus wilt wissen? Dit zet de override automatisch uit.",
      )
    ) {
      return;
    }
    setClearing(true);
    try {
      const r = await adminApi.delete(
        `/admin/shipping-labels/${label.id}/custom-status`,
      );
      setLabel(r.data);
      toast.success("Custom status gewist");
      onChanged && onChanged();
      setHistory([]);
    } catch (err) {
      toast.error(formatApiError(err) || "Wissen mislukt");
    } finally {
      setClearing(false);
    }
  };

  const applyPreset = async ({ activateOverride = false } = {}) => {
    if (!selectedPresetId) {
      toast.error("Kies eerst een preset uit de dropdown.");
      return;
    }
    if (activateOverride && isMedewerker) {
      toast.error("Alleen Owner/Admin/Manager mag override activeren.");
      return;
    }
    setApplyingPreset(true);
    try {
      const r = await adminApi.post(
        `/admin/shipping-labels/${label.id}/apply-status-preset`,
        { preset_id: selectedPresetId, activate_override: !!activateOverride },
      );
      setLabel(r.data);
      toast.success(
        activateOverride
          ? "Preset toegepast en override geactiveerd"
          : "Preset toegepast — pas tekst aan en sla op om de override te activeren.",
      );
      onChanged && onChanged();
      setHistory([]);
      setSelectedPresetId("");
    } catch (err) {
      toast.error(formatApiError(err) || "Preset toepassen mislukt");
    } finally {
      setApplyingPreset(false);
    }
  };

  const handleApplyPresetClick = async () => {
    if (!selectedPresetId) {
      toast.error("Kies eerst een preset uit de dropdown.");
      return;
    }
    // Als override al aan staat, gewoon toepassen — niets vragen
    if (overrideActive) {
      await applyPreset({ activateOverride: false });
      return;
    }
    // Anders vragen we of ze direct override willen activeren (alleen indien toegestaan)
    if (!isMedewerker) {
      const yes = window.confirm(
        "Preset wordt op het label gezet. Wilt u deze status ook DIRECT actief maken voor publieke tracking (override aan)?\n\n[OK] = opslaan + override aan\n[Annuleer] = alleen opslaan",
      );
      await applyPreset({ activateOverride: !!yes });
    } else {
      // Medewerker — alleen opslaan, geen override
      await applyPreset({ activateOverride: false });
    }
  };

  // ---------- Status source badge ----------
  const linked = label.linked_external_tracking_number;
  let sourceBadge;
  if (overrideActive && existing?.code) {
    sourceBadge = {
      label: "5999Cargo custom status actief",
      cls: "bg-emerald-100 text-emerald-800 border-emerald-300",
      icon: <Eye className="w-3 h-3" />,
    };
  } else if (linked) {
    sourceBadge = {
      label: "Externe partnerstatus actief",
      cls: "bg-indigo-100 text-indigo-800 border-indigo-300",
      icon: <Globe className="w-3 h-3" />,
    };
  } else {
    sourceBadge = {
      label: "Interne label-status actief",
      cls: "bg-gray-100 text-gray-700 border-gray-300",
      icon: <Eye className="w-3 h-3" />,
    };
  }

  return (
    <div
      className="bg-gradient-to-br from-[#082c50]/[0.03] to-cyan-50/40 border border-[#082c50]/15 rounded-md p-3 my-3"
      data-testid="custom-status-section"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#082c50]" />
          <h4 className="font-chivo font-bold text-xs text-[#082c50] uppercase tracking-wide">
            Trackingstatus CMS
          </h4>
        </div>
        <span
          className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 border rounded ${sourceBadge.cls}`}
          data-testid="custom-status-source-badge"
        >
          {sourceBadge.icon}
          {sourceBadge.label}
        </span>
      </div>

      {/* Override toggle */}
      <div
        className="flex items-center justify-between gap-3 p-3 mb-3 bg-white border border-gray-200 rounded-md"
        data-testid="custom-status-override-row"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-[#082c50]">
              Custom status als publieke status tonen
            </span>
            {isMedewerker && (
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase font-bold px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-300 rounded"
                title="Alleen Owner / Admin / Manager kan de override toggle bedienen"
                data-testid="custom-status-override-locked-badge"
              >
                <Lock className="w-2.5 h-2.5" />
                Vergrendeld
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-600 mt-0.5">
            {overrideActive
              ? "AAN — klanten zien de 5999Cargo custom status op de publieke trackingpagina."
              : "UIT — externe partnerstatus (W-koppeling) of interne label-status is leidend voor de klant."}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={overrideActive}
          aria-label="Override toggle"
          disabled={isMedewerker || togglingOverride || (!existing && !overrideActive)}
          onClick={handleToggleOverride}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            overrideActive ? "bg-emerald-500" : "bg-gray-300"
          }`}
          data-testid="custom-status-override-toggle"
        >
          {togglingOverride ? (
            <Loader2 className="w-3 h-3 text-white absolute left-1.5 animate-spin" />
          ) : null}
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
              overrideActive ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Preset-dropdown (Sprint 2 Fase 2) */}
      {presetsLoaded && presets.length > 0 && (
        <div
          className="flex items-center gap-2 p-3 mb-3 bg-white border border-gray-200 rounded-md flex-wrap"
          data-testid="custom-status-preset-row"
        >
          <Wand2 className="w-4 h-4 text-[#082c50] flex-shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] uppercase font-bold text-gray-600">
              Status-preset kiezen
            </Label>
            <select
              value={selectedPresetId}
              onChange={(e) => setSelectedPresetId(e.target.value)}
              className="w-full rounded-md border border-gray-300 text-xs px-2 py-1 mt-0.5 bg-white"
              data-testid="custom-status-preset-select"
            >
              <option value="">— Kies een preset —</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.public_label} ({p.progress_percent}%)
                </option>
              ))}
            </select>
          </div>
          <Button
            size="sm"
            type="button"
            onClick={handleApplyPresetClick}
            disabled={!selectedPresetId || applyingPreset}
            className="bg-[#082c50] text-white rounded-md text-xs h-9"
            data-testid="custom-status-preset-apply"
          >
            {applyingPreset ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Wand2 className="w-3 h-3 mr-1" />
            )}
            Preset toepassen
          </Button>
        </div>
      )}

      {/* Form */}
      <div className="space-y-2 text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">
              Statuscode *
            </Label>
            <Input
              value={form.code}
              onChange={(e) => setField("code", slugifyCode(e.target.value))}
              placeholder="bv. in_douane_behandeling"
              maxLength={80}
              className="rounded-md text-xs font-mono"
              data-testid="custom-status-code"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">
              Voortgang: {form.progress_percent}%
            </Label>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={form.progress_percent}
              onChange={(e) =>
                setField("progress_percent", Number(e.target.value))
              }
              className="w-full mt-2 accent-[#082c50]"
              data-testid="custom-status-progress"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">
              Publiek label NL *
            </Label>
            <Input
              value={form.public_label}
              onChange={(e) => setField("public_label", e.target.value)}
              placeholder="bv. In behandeling bij douane"
              maxLength={120}
              className="rounded-md text-xs"
              data-testid="custom-status-public-label-nl"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">
              Publiek label EN
            </Label>
            <Input
              value={form.public_label_en}
              onChange={(e) => setField("public_label_en", e.target.value)}
              placeholder="e.g. In customs handling"
              maxLength={120}
              className="rounded-md text-xs"
              data-testid="custom-status-public-label-en"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">
              Publieke boodschap NL
            </Label>
            <Textarea
              value={form.public_message}
              onChange={(e) => setField("public_message", e.target.value)}
              placeholder="Uitleg die de klant te zien krijgt op /tracking"
              maxLength={600}
              rows={3}
              className="rounded-md text-xs"
              data-testid="custom-status-public-message-nl"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">
              Publieke boodschap EN
            </Label>
            <Textarea
              value={form.public_message_en}
              onChange={(e) => setField("public_message_en", e.target.value)}
              placeholder="English explanation shown to the customer"
              maxLength={600}
              rows={3}
              className="rounded-md text-xs"
              data-testid="custom-status-public-message-en"
            />
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase font-bold text-gray-600 flex items-center gap-1">
            <EyeOff className="w-3 h-3" />
            Interne notitie (nooit publiek)
          </Label>
          <Textarea
            value={form.internal_note}
            onChange={(e) => setField("internal_note", e.target.value)}
            placeholder="Operationele notitie voor backoffice — wordt nooit naar de klant getoond"
            maxLength={1000}
            rows={2}
            className="rounded-md text-xs bg-amber-50/50 border-amber-200"
            data-testid="custom-status-internal-note"
          />
          <p className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1">
            <AlertTriangle className="w-2.5 h-2.5" />
            Deze tekst is alleen zichtbaar voor backoffice-medewerkers.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={savingForm || !formValid}
            className="bg-[#082c50] text-white rounded-md text-xs"
            data-testid="custom-status-save"
          >
            {savingForm ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Save className="w-3 h-3 mr-1" />
            )}
            {existing ? "Bijwerken" : "Opslaan"}
          </Button>
          {existing && !isMedewerker && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleClear}
              disabled={clearing}
              className="rounded-md text-xs border-red-200 text-red-700 hover:bg-red-50"
              data-testid="custom-status-clear"
            >
              {clearing ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3 mr-1" />
              )}
              Wissen
            </Button>
          )}
        </div>
      </div>

      {/* Customer preview */}
      {existing?.code && (
        <CustomerPreview
          customStatus={existing}
          active={overrideActive}
        />
      )}

      {/* History */}
      <div className="mt-3 border-t border-gray-200 pt-2">
        <button
          type="button"
          onClick={toggleHistory}
          className="flex items-center gap-2 text-xs font-bold text-[#082c50] hover:underline"
          data-testid="custom-status-history-toggle"
        >
          <HistoryIcon className="w-3 h-3" />
          Statushistorie
          {historyOpen ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
        </button>
        {historyOpen && (
          <div
            className="mt-2 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md"
            data-testid="custom-status-history-list"
          >
            {historyLoading ? (
              <div className="p-4 text-center text-xs text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
                Laden…
              </div>
            ) : history.length === 0 ? (
              <div
                className="p-4 text-center text-xs text-gray-500"
                data-testid="custom-status-history-empty"
              >
                Nog geen mutaties geregistreerd.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {history.map((it) => (
                  <li
                    key={it.id}
                    className="p-2 text-xs"
                    data-testid={`custom-status-history-row-${it.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-bold text-[#082c50]">
                        {HISTORY_ACTION_LABELS[it.action] || it.action}
                      </span>
                      <span className="text-gray-500 text-[10px]">
                        {fmtDate(it.created_at)}
                      </span>
                    </div>
                    <div className="text-gray-600 mt-0.5">
                      door {it.actor_email || "—"}
                      {it.actor_role ? ` (${it.actor_role})` : ""}
                    </div>
                    {it.new_value?.public_label && (
                      <div className="text-gray-500 mt-0.5">
                        Label: <span className="font-mono">{it.new_value.public_label}</span>
                      </div>
                    )}
                    {typeof it.new_value?.progress_percent === "number" && (
                      <div className="text-gray-500">
                        Voortgang: {it.new_value.progress_percent}%
                      </div>
                    )}
                    {it.new_value?.has_internal_note && (
                      <div className="text-amber-700 text-[10px] mt-0.5">
                        (interne notitie aanwezig — verborgen voor audit)
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerPreview({ customStatus, active }) {
  const pct = Math.max(0, Math.min(100, customStatus.progress_percent || 0));
  return (
    <div
      className="mt-3 rounded-md border border-dashed border-emerald-300 bg-emerald-50/40 p-3"
      data-testid="custom-status-customer-preview"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase font-bold text-emerald-800">
          Voorvertoning klantweergave
        </span>
        <span
          className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${
            active
              ? "bg-emerald-100 text-emerald-800 border-emerald-300"
              : "bg-gray-100 text-gray-600 border-gray-300"
          }`}
        >
          {active ? "Live zichtbaar" : "Niet zichtbaar — toggle uit"}
        </span>
      </div>
      <div className="bg-white border border-gray-200 rounded-md p-3">
        <div className="text-xs font-bold text-[#082c50]">
          {customStatus.public_label}
        </div>
        {customStatus.public_message && (
          <p className="text-xs text-gray-700 mt-1 leading-relaxed">
            {customStatus.public_message}
          </p>
        )}
        <div className="mt-2">
          <div className="flex items-center justify-between text-[10px] uppercase font-bold text-[#082c50] mb-1">
            <span>Voortgang</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-[#082c50]/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#1a5193] to-cyan-400 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
      <p className="text-[10px] text-emerald-700 mt-1.5 flex items-center gap-1">
        <EyeOff className="w-2.5 h-2.5" /> Interne notitie wordt nooit publiek
        getoond.
      </p>
    </div>
  );
}
